import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import { db } from '../config/firebase';
import { collection, getDocs, query, limit, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

export default function GroupDiagnostics() {
  const { currentUser } = useAuth();
  const [diagnostics, setDiagnostics] = useState({});
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState('');

  const runDiagnostics = useCallback(async () => {
    setLoading(true);
    const results = {};

    try {
      // Test 1: Basic Firestore connection
      results.firestore = '✅ Connected';
    } catch (error) {
      results.firestore = `❌ Error: ${error.message}`;
    }

    try {
      // Test 2: Groups collection access
      const testQuery = query(collection(db, 'groups'), limit(1));
      await getDocs(testQuery);
      results.groupsCollection = '✅ Can access groups collection';
    } catch (error) {
      results.groupsCollection = `❌ Error: ${error.message}`;
    }

    try {
      // Test 3: Create test group (if user is authenticated)
      if (currentUser) {
        const testGroup = {
          name: 'Test Group',
          description: 'Diagnostic test group',
          members: [currentUser.uid],
          memberCount: 1,
          createdBy: currentUser.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          isActive: true
        };
        
        const docRef = await addDoc(collection(db, 'groups'), testGroup);
        results.createGroup = `✅ Can create groups (test group ID: ${docRef.id})`;
        
        // Clean up test group
        // Note: In production, you'd want to delete this test group
      } else {
        results.createGroup = '⚠️ No user authenticated';
      }
    } catch (error) {
      results.createGroup = `❌ Error: ${error.message}`;
    }

    try {
      // Test 4: Complex query (the one that's failing)
      const complexQuery = query(
        collection(db, 'groups'),
        limit(1)
      );
      await getDocs(complexQuery);
      results.complexQuery = '✅ Complex query works';
    } catch (error) {
      results.complexQuery = `❌ Error: ${error.message}`;
    }

    setDiagnostics(results);
    setLoading(false);
  }, [currentUser]);

  const testSimpleQuery = async () => {
    try {
      setTestResult('Testing simple query...');
      
      const simpleQuery = query(collection(db, 'groups'), limit(1));
      const snapshot = await getDocs(simpleQuery);
      
      setTestResult(`✅ Simple query successful! Found ${snapshot.size} documents.`);
    } catch (error) {
      setTestResult(`❌ Simple query failed: ${error.message}`);
    }
  };

  useEffect(() => {
    runDiagnostics();
  }, [runDiagnostics]);

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        🔧 Group Diagnostics
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        This will help identify why groups aren't loading:
      </Typography>

      <List>
        {Object.entries(diagnostics).map(([key, value]) => (
          <React.Fragment key={key}>
            <ListItem>
              <ListItemText 
                primary={key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')} 
                secondary={value}
              />
            </ListItem>
            <Divider />
          </React.Fragment>
        ))}
      </List>

      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Button 
          variant="contained" 
          onClick={runDiagnostics}
          disabled={loading}
        >
          {loading ? 'Running...' : 'Run Diagnostics'}
        </Button>
        
        <Button 
          variant="outlined" 
          onClick={testSimpleQuery}
        >
          Test Simple Query
        </Button>
      </Box>

      {testResult && (
        <Alert severity={testResult.includes('✅') ? 'success' : 'error'} sx={{ mt: 2 }}>
          {testResult}
        </Alert>
      )}

      <Box sx={{ mt: 3, p: 2, backgroundColor: '#f0f0f0', borderRadius: 1 }}>
        <Typography variant="body2" component="div">
          <strong>Common Solutions:</strong>
          <br />
          1. <strong>Permission Denied:</strong> Update Firestore security rules
          <br />
          2. <strong>Missing Index:</strong> Create index for 'members' + 'createdAt'
          <br />
          3. <strong>Collection Not Found:</strong> Groups collection will be auto-created
        </Typography>
      </Box>
    </Paper>
  );
}
